import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const MOJIBAKE_PATTERNS = [
  /\u00C3[\u00A0-\u00BF]/,
  /\u00C2(?:[\u0080-\u00BF]| )/,
  /\u00E2[\u0080-\u00BF]{1,2}/,
  /\uFFFD/,
  /\u00EF\u00BF\u00BD/,
];

const countMojibakeArtifacts = (text) => {
  if (!text) return 0;
  return MOJIBAKE_PATTERNS.reduce((total, pattern) => {
    const globalPattern = new RegExp(pattern.source, 'g');
    return total + (text.match(globalPattern)?.length || 0);
  }, 0);
};

const hasMojibakeArtifacts = (text) => {
  if (!text) return false;
  return MOJIBAKE_PATTERNS.some((pattern) => pattern.test(text));
};

const CP1252_EXTRA_CHAR_TO_BYTE = {
  0x20AC: 0x80,
  0x201A: 0x82,
  0x0192: 0x83,
  0x201E: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02C6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8A,
  0x2039: 0x8B,
  0x0152: 0x8C,
  0x017D: 0x8E,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201C: 0x93,
  0x201D: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02DC: 0x98,
  0x2122: 0x99,
  0x0161: 0x9A,
  0x203A: 0x9B,
  0x0153: 0x9C,
  0x017E: 0x9E,
  0x0178: 0x9F
};

const decodeLikelyLatin1Utf8 = (text) => {
  const bytes = new Uint8Array(text.length);
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    if (code <= 255) {
      bytes[index] = code;
      continue;
    }
    const mapped = CP1252_EXTRA_CHAR_TO_BYTE[code];
    if (mapped === undefined) {
      return text;
    }
    bytes[index] = mapped;
  }

  try {
    return new TextDecoder('utf-8').decode(bytes);
  } catch (error) {
    return text;
  }
};

export function normalizeDisplayText(value) {
  if (typeof value !== 'string' || !value) return value;

  let current = value;
  for (let index = 0; index < 3; index += 1) {
    if (!hasMojibakeArtifacts(current)) break;

    const candidate = decodeLikelyLatin1Utf8(current);
    if (!candidate || candidate === current) break;

    if (countMojibakeArtifacts(candidate) <= countMojibakeArtifacts(current)) {
      current = candidate;
    } else {
      break;
    }
  }

  return current;
}

export function normalizeObjectStrings(input) {
  if (Array.isArray(input)) {
    return input.map((item) => normalizeObjectStrings(item));
  }

  if (input && typeof input === 'object') {
    return Object.fromEntries(
      Object.entries(input).map(([key, value]) => [key, normalizeObjectStrings(value)])
    );
  }

  return normalizeDisplayText(input);
}

const OAUTH_ERROR_MESSAGES = {
  google_unavailable: 'Login com Google está indisponível no momento.',
  google_access_denied: 'Você cancelou ou negou o acesso da conta Google.',
  google_email_not_verified: 'Sua conta Google precisa ter um e-mail verificado para entrar.',
  google_session_expired: 'Sua sessão do Google expirou. Tente novamente.',
  google_auth_failed: 'Não foi possível concluir o login com Google. Tente novamente.',
  partner_code_invalid: 'O cupom de parceiro informado não está válido no momento.',
};

export function getOAuthErrorMessage(errorCode) {
  const normalizedCode = String(errorCode || '').trim();
  return OAUTH_ERROR_MESSAGES[normalizedCode] || OAUTH_ERROR_MESSAGES.google_auth_failed;
}


